import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../../../api/agent";
import { AppResponse } from "../../../../models/app/AppResponse";
import {
  CartItem,
  ShoppingPortalDto,
  ShoppingPortalInitialState,
} from "../../../../models/features/ShoppingPortal/ShoppingPortalDto";
import { DraftOrderRequest } from "../../../../models/features/Gravity/CheckoutDetail";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { TargetOrderRequest } from "../../../../models/features/ShoppingPortal/ShoppingPortalOrderHookRequest";
import {
  DmsPart,
  DmsPartRequestDto,
} from "../../../../models/features/ShoppingPortal/DmsPart";
import {
  DmsCategory,
  DmsManufacturer,
} from "../../../../models/features/ShoppingPortal/DmsManufacturer";
import { ProductManagerFilter } from "../../../../models/app/AppModels";
import { PartsPortalType } from "../../../../models/features/ShopifySetting/ShoppingPortal/ShoppingPortal";

export const shoppingPortalApi = createApi({
  reducerPath: "shoppingPortalApi",
  baseQuery: axiosBaseQuery(),

  endpoints: (build) => ({
    getPortalSetting: build.mutation<
      AppResponse<ShoppingPortalDto>,
      { storeUrl: string }
    >({
      query: ({ storeUrl }) => ({
        url: `api/ShoppingPortal/GetPortalSetting?storeUrl=${storeUrl}`,
        method: "post",
      }),
    }),
    calculateTax: build.mutation<
      any,
      { data: DraftOrderRequest; dmsSetupId: string }
    >({
      query: ({ data, dmsSetupId }) => ({
        url: `api/Order/CalculatedDraftOrder?dmsSetupId=${dmsSetupId}`,
        method: "post",
        data: data,
      }),
    }),
    createShoppingPortalOrder: build.mutation<
      any,
      { data: TargetOrderRequest }
    >({
      query: ({ data }) => ({
        url: `api/PortalOrder/CreateOrder`,
        method: "post",
        data: data,
      }),
    }),
    addPayPalTransactionHistory: build.mutation<any, { data: any }>({
      query: ({ data }) => ({
        url: `api/AddPayPalTransactionHistory`,
        method: "post",
        data: data,
      }),
    }),
    getPayPalClientId: build.mutation<any, { setupId: any }>({
      query: ({ setupId }) => ({
        url: `api/GetPayPalClientId?setupId=${setupId}`,
        method: "get",
      }),
    }),
    getDmsCategories: build.mutation<
      AppResponse<DmsCategory>,
      { dealerIds: string[] }
    >({
      query: ({ dealerIds }) => ({
        url: `api/DmsSetup/GetDmsCategories`,
        method: "post",
        data: {
          dealerIds: dealerIds,
        },
      }),
    }),
    getDmsManufacturers: build.query<
      AppResponse<DmsManufacturer>,
      { dealerId: string }
    >({
      query: ({ dealerId }) => ({
        url: `api/Manufacturer/GetDmsManufacturers?dealerId=${dealerId}`,
        method: "get",
      }),
    }),
    getMappedDmsManufacturers: build.mutation<
      AppResponse<DmsManufacturer>,
      { dealerId: string }
    >({
      query: ({ dealerId }) => ({
        url: `api/Manufacturer/GetMappedDmsManufacturers?dealerId=${dealerId}`,
        method: "get",
      }),
    }),
    getDmsParts: build.mutation<
      AppResponse<DmsPart>,
      { data: DmsPartRequestDto }
    >({
      query: ({ data }) => ({
        url: `api/ShoppingPortal/GetDmsParts`,
        method: "post",
        data: data,
      }),
    }),
    getSyncedWholegoods: build.mutation<
      any,
      { setupId: string; paging: ProductManagerFilter; productType: number }
    >({
      query: ({ paging, setupId }) => ({
        url: `api/ShoppingPortal/GetSyncedWholegoods?setupId=${setupId}`,
        method: "post",
        data: paging,
      }),
    }),
    AddToCartPart: build.mutation<any, { dmsSetupId: string; part: DmsPart }>({
      query: (request) => ({
        url: `api/Shopify/AddToCartPart`,
        method: "post",
        data: { request: request },
      }),
    }),
  }),
});

export const shoppingPortalSlice = createSlice({
  name: "ShoppingPortal",
  initialState: ShoppingPortalInitialState,
  reducers: {
    addPortalSettings: (
      state,
      action: PayloadAction<{
        settings: ShoppingPortalDto;
        storeUrl: string;
        logoUrl: string;
        expiryTime: number;
      }>
    ) => {
      state.storeUrl = action.payload.storeUrl;
      state.portalSettings = action.payload.settings;
      state.logoUrl = action.payload.logoUrl;
      state.expiryTime = action.payload.expiryTime;
    },
    addToCart: (
      state,
      action: PayloadAction<{
        cartItem: CartItem;
      }>
    ) => {
      state.cart?.push(action.payload.cartItem);
    },
    removeFromCart: (
      state,
      action: PayloadAction<{ inventoryPartId: string }>
    ) => {
      state.cart = state?.cart?.filter(
        (item) => item.inventoryPartId !== action.payload.inventoryPartId
      );
    },
    removeCart: (state) => {
      state.cart = [];
    },
    updateCartItemQuantity: (
      state,
      action: PayloadAction<{ inventorypartId: string; quantity: number }>
    ) => {
      let itemIndex = state?.cart?.findIndex(
        (item) => item.inventoryPartId == action.payload.inventorypartId
      );
      state.cart[itemIndex].quantity = action.payload.quantity;
    },
    setOpenCheckout: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.openCheckout = action.payload.isOpen;
    },
    removePortalSetting: (state) => {
      state.logoUrl = undefined;
      state.openCheckout = false;
      state.portalSettings = undefined;
      state.cart = [];
      state.storeUrl = undefined;
    },
    setPartsPortalEnable: (
      state,
      action: PayloadAction<{ isEnabled: boolean }>
    ) => {
      state.isPortalEnabled = action.payload.isEnabled;
    },
    setPartsPortalType: (
      state,
      action: PayloadAction<{ type: PartsPortalType }>
    ) => {
      state.portalType = action.payload.type;
    },
  },
});

export const {
  addPortalSettings,
  addToCart,
  removeFromCart,
  removeCart,
  updateCartItemQuantity,
  setOpenCheckout,
  removePortalSetting,
  setPartsPortalEnable,
  setPartsPortalType,
} = shoppingPortalSlice.actions;

export const {
  useGetPortalSettingMutation,
  useCalculateTaxMutation,
  useCreateShoppingPortalOrderMutation,
  useAddPayPalTransactionHistoryMutation,
  useGetPayPalClientIdMutation,
  useGetDmsManufacturersQuery,
  useGetDmsPartsMutation,
  useGetSyncedWholegoodsMutation,
  useGetMappedDmsManufacturersMutation,
  useAddToCartPartMutation,
  useGetDmsCategoriesMutation,
} = shoppingPortalApi;
export const shoppingPortalReducer = shoppingPortalSlice.reducer;
